import React, { useEffect, useRef, useState } from 'react';

const MapaUbicacion = ({ obra, setObra }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Geocodificación inversa para obtener dirección (versión compacta)
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      
      // Construir dirección compacta con las partes más relevantes
      const address = data.address || {};
      const parts = [];
      
      // Calle y número
      if (address.road) {
        const calle = address.house_number 
          ? `${address.road}, ${address.house_number}` 
          : address.road;
        parts.push(calle);
      }
      
      // Ciudad/Pueblo
      if (address.city) {
        parts.push(address.city);
      } else if (address.town) {
        parts.push(address.town);
      } else if (address.village) {
        parts.push(address.village);
      } else if (address.municipality) {
        parts.push(address.municipality);
      }
      
      // Provincia
      if (address.state || address.province) {
        parts.push(address.state || address.province);
      }
      
      // Si no hay partes, usar display_name pero truncado
      const shortAddress = parts.length > 0 
        ? parts.join(', ') 
        : (data.display_name || '').substring(0, 100);
      
      // Asegurar que no exceda 100 caracteres
      return shortAddress.length > 100 
        ? shortAddress.substring(0, 97) + '...' 
        : shortAddress;
        
    } catch (error) {
      console.error('Error en geocodificación:', error);
      return '';
    }
  };

  // Función para parsear LatLong string a coordenadas
  const parseLatLong = (latLongString) => {
    if (!latLongString || typeof latLongString !== 'string') return null;
    const parts = latLongString.split(',').map(p => parseFloat(p.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parts[0], lng: parts[1] };
    }
    return null;
  };

  // Inicializar mapa
  useEffect(() => {
    if (!window.L || mapInstanceRef.current || !mapContainerRef.current) return;

    // Intentar obtener coordenadas de LatLong string o de Latitud/Longitud
    let initialLat = 40.4168; // España centro por defecto
    let initialLng = -3.7038;
    let hasCoordinates = false;

    // Prioridad 1: LatLong string
    if (obra?.LatLong) {
      const coords = parseLatLong(obra.LatLong);
      if (coords) {
        initialLat = coords.lat;
        initialLng = coords.lng;
        hasCoordinates = true;
      }
    }
    
    // Prioridad 2: Latitud y Longitud separados
    if (!hasCoordinates && obra?.Latitud && obra?.Longitud) {
      initialLat = obra.Latitud;
      initialLng = obra.Longitud;
      hasCoordinates = true;
    }

    const initialZoom = hasCoordinates ? 15 : 6;

    // Crear mapa
    const map = window.L.map(mapContainerRef.current, {
      attributionControl: false,
      zoomControl: true
    }).setView([initialLat, initialLng], initialZoom);

    // Agregar tile layer
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Si hay coordenadas, agregar marcador círculo azul
    if (hasCoordinates) {
      const marker = window.L.circleMarker([initialLat, initialLng], {
        color: '#ffffff',
        fillColor: '#2563eb',
        fillOpacity: 1,
        weight: 4,
        radius: 15
      }).addTo(map);

      markerRef.current = marker;
    }

    // Evento de clic en el mapa
    map.on('click', async function(e) {
      const { lat, lng } = e.latlng;

      // Remover marcador anterior si existe
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
      }

      // Crear nuevo marcador círculo azul más grande y llamativo
      const marker = window.L.circleMarker([lat, lng], {
        color: '#ffffff',
        fillColor: '#2563eb',
        fillOpacity: 1,
        weight: 4,
        radius: 15
      }).addTo(map);

      markerRef.current = marker;

      // Formatear LatLong como "lat, lng"
      const latLong = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

      // Actualizar coordenadas usando función de callback para preservar el estado actual
      setObra(prev => ({
        ...prev,
        Latitud: lat,
        Longitud: lng,
        Direccion: 'Cargando...',
        LatLong: latLong
      }));

      // Obtener dirección automáticamente
      const address = await reverseGeocode(lat, lng);
      setObra(prev => ({
        ...prev,
        Latitud: lat,
        Longitud: lng,
        Direccion: address,
        LatLong: latLong
      }));
    });

    mapInstanceRef.current = map;

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  // Actualizar marcador cuando cambien las coordenadas externamente
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    let lat, lng;
    let hasCoordinates = false;

    // Prioridad 1: LatLong string
    if (obra?.LatLong) {
      const coords = parseLatLong(obra.LatLong);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
        hasCoordinates = true;
      }
    }

    // Prioridad 2: Latitud y Longitud separados
    if (!hasCoordinates && obra?.Latitud && obra?.Longitud) {
      lat = obra.Latitud;
      lng = obra.Longitud;
      hasCoordinates = true;
    }

    if (hasCoordinates) {
      // Remover marcador anterior
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }

      // Crear nuevo marcador
      const marker = window.L.circleMarker([lat, lng], {
        color: '#ffffff',
        fillColor: '#7C3AED',
        fillOpacity: 1,
        weight: 4,
        radius: 15
      }).addTo(mapInstanceRef.current);

      markerRef.current = marker;

      // Centrar mapa en el marcador
      mapInstanceRef.current.setView([lat, lng], 15);
    }
  }, [obra?.Latitud, obra?.Longitud, obra?.LatLong]);

  // Efecto para redimensionar el mapa cuando cambia fullscreen
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Invalidar tamaño después de cambiar
    setTimeout(() => {
      mapInstanceRef.current.invalidateSize();
    }, 100);
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Función para obtener ubicación actual
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización');
      return;
    }

    setIsLoadingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;

        // Remover marcador anterior si existe
        if (markerRef.current && mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(markerRef.current);
        }

        // Crear nuevo marcador
        if (mapInstanceRef.current) {
          const marker = window.L.circleMarker([lat, lng], {
            color: '#ffffff',
            fillColor: '#10b981', // Verde para indicar ubicación actual
            fillOpacity: 1,
            weight: 4,
            radius: 15
          }).addTo(mapInstanceRef.current);

          markerRef.current = marker;

          // Centrar mapa en la ubicación
          mapInstanceRef.current.setView([lat, lng], 15);
        }

        // Formatear LatLong
        const latLong = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

        // Actualizar estado
        setObra(prev => ({
          ...prev,
          Latitud: lat,
          Longitud: lng,
          Direccion: 'Cargando dirección...',
          LatLong: latLong
        }));

        // Obtener dirección
        const address = await reverseGeocode(lat, lng);
        setObra(prev => ({
          ...prev,
          Direccion: address
        }));

        setIsLoadingLocation(false);
      },
      (error) => {
        setIsLoadingLocation(false);
        let errorMessage = 'No se pudo obtener la ubicación';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado. Por favor, habilita la ubicación en tu navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Información de ubicación no disponible';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tiempo de espera agotado al obtener ubicación';
            break;
          default:
            errorMessage = 'Error desconocido al obtener ubicación';
        }
        
        setLocationError(errorMessage);
        
        // Limpiar error después de 5 segundos
        setTimeout(() => setLocationError(null), 5000);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <div 
      className={`transition-all duration-300 ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 lg:p-8' 
          : 'relative w-full h-full'
      }`}
    >
      {/* Botón de ubicación actual */}
      <button
        onClick={handleGetCurrentLocation}
        disabled={isLoadingLocation}
        className={`absolute bg-white hover:bg-gray-50 text-gray-700 transition-all duration-200 hover:scale-105 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          isFullscreen ? 'bottom-10 left-10' : 'bottom-4 left-4'
        }`}
        style={{ 
          zIndex: 1001,
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
        title="Usar mi ubicación actual"
      >
        {isLoadingLocation ? (
          // Spinner de carga
          <svg className="w-5 h-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          // Icono de ubicación
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )}
      </button>

      {/* Mensaje de error */}
      {locationError && (
        <div 
          className={`absolute bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-lg ${
            isFullscreen ? 'bottom-10 left-20' : 'bottom-4 left-20'
          }`}
          style={{ 
            zIndex: 1001,
            maxWidth: '300px',
            fontSize: '13px'
          }}
        >
          {locationError}
        </div>
      )}

      {/* Botón de pantalla completa/minimizar */}
      <button
        onClick={toggleFullscreen}
        className={`absolute bg-white hover:bg-gray-50 text-gray-700 transition-all duration-200 hover:scale-105 border border-gray-200 ${
          isFullscreen ? 'top-10 right-10' : 'top-4 right-4'
        }`}
        style={{ 
          zIndex: 1001,
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
        title={isFullscreen ? 'Minimizar mapa' : 'Ver en pantalla completa'}
      >
        {isFullscreen ? (
          // Icono minimizar (X)
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          // Icono expandir
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        )}
      </button>

      <div 
        ref={mapContainerRef} 
        className="w-full rounded-lg overflow-hidden shadow-sm border"
        style={{ 
          height: isFullscreen ? 'calc(100vh - 4rem)' : '100%',
          minHeight: isFullscreen ? 'auto' : '400px'
        }}
      >
        <style jsx="true">{`
          .leaflet-control-zoom {
            border: none !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
            border-radius: 12px !important;
            overflow: hidden;
          }
          
          .leaflet-control-zoom a {
            width: 36px !important;
            height: 36px !important;
            line-height: 36px !important;
            font-size: 20px !important;
            font-weight: 600 !important;
            color: #374151 !important;
            background: #ffffff !important;
            border: none !important;
            transition: all 0.2s ease !important;
          }
          
          .leaflet-control-zoom a:hover {
            background: #f3f4f6 !important;
            color: #111827 !important;
            transform: scale(1.05);
          }
          
          .leaflet-control-zoom a:first-child {
            border-bottom: 1px solid #e5e7eb !important;
          }
          
          .leaflet-control-zoom-in {
            border-radius: 12px 12px 0 0 !important;
          }
          
          .leaflet-control-zoom-out {
            border-radius: 0 0 12px 12px !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default MapaUbicacion;
